import {
  type Country,
  type Currency,
  CURRENCY_TRANSLATED,
  type Feature,
  type Module,
  type SerializedCustomer,
  type SerializedCustomerMe,
  type Subject,
  type UserPopulate,
  UserRole,
} from '@enmon/commons/lib/enmon';
import { type Commodity, isCommodity } from '@enmon/commons/lib/shared';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AbilityContext } from '@/containers/AuthenticatedApp/AbilityContext';
import { useAbility } from '@casl/react';
import type { DeprecatedPermission } from '@/types/DeprecatedPermission';
import type { ForcedSubject } from '@casl/ability';
import { isNil } from 'ramda';
import type { PopulatedUser } from '@/services/api/users';
import { isAction, isResource, Resource, type ResourceAction } from '@enmon/commons/lib/shared/permissions';
import {
  getAppContext,
  getCustomerCurrency,
  selectActiveCustomer,
  selectActiveUser,
  selectCustomerCountry,
  selectCustomerId,
  selectHasFeature,
  selectHasModule,
} from './selectors';
import {
  type AvailableCommoditiesShape,
  type AvailableConsumptionCommoditiesShape,
  selectAvailableCommodities,
  selectAvailableConsumptionCommodities,
} from '../AuthenticatedApp/selectors';

export const useAppContext = (): 'magic' | 'parent' | 'child' => useSelector(getAppContext);

/**
 * @deprecated Use `useActiveUserNew` instead
 */
export const useActiveUser = (): PopulatedUser<UserPopulate.Customer> | undefined => useSelector(selectActiveUser);

/**
 * Like `useActiveUser`, but throws an error if no user is found.
 * @throws Error if no user is found. Are you logged in?
 * @returns The currently active authenticated user
 * @description Use this hook only inside authenticated context.
 */
export const useActiveUserNew = (): PopulatedUser<UserPopulate.Customer> => {
  const user = useActiveUser();

  if (!user) {
    throw Error('User not found. Are you logged in?');
  }

  return user;
};

export const useIncludeVat = (): boolean => useActiveUserNew().includeVat;

/**
 * @deprecated Use `useActiveCustomerNew` instead
 */
export const useActiveCustomer = (): SerializedCustomerMe['customer'] | null | undefined =>
  useSelector(selectActiveCustomer);

/**
 * @throws Error if no customer is found. Are you logged in as a customer?
 * @returns customer user is currently logged in as
 */
export const useActiveCustomerNew = (): SerializedCustomerMe['customer'] => {
  const customer = useActiveCustomer();

  if (!customer) {
    throw Error('Customer not found. Are you logged in as a customer?');
  }

  return customer;
};

export const useCustomerId = (): SerializedCustomer['_id'] | undefined => useSelector(selectCustomerId);

export const useCustomerCountry = (): Country | undefined => useSelector(selectCustomerCountry);

export type HasFeatureFn = (feature: Feature) => boolean;
export const useHasFeature = (): HasFeatureFn => useSelector(selectHasFeature);

export type HasModuleFn = (module: Module) => boolean;
export const useHasModule = (): HasModuleFn => useSelector(selectHasModule);

export const useCustomerCurrency = (): Currency | undefined => useSelector(getCustomerCurrency);

export const useCustomerCurrencyReadable = (): string => {
  const currency = useCustomerCurrency();
  return !isNil(currency) ? CURRENCY_TRANSLATED[currency] : '';
};

export const useAvailableCommodities = (options?: { onlyConsumption?: boolean }): Commodity[] => {
  const { onlyConsumption } = options ?? {};
  const config = useSelector(onlyConsumption ? selectAvailableConsumptionCommodities : selectAvailableCommodities);
  return useMemo(() => Object.keys(config).filter(isCommodity), [config]);
};

export const useAvailableCommoditiesConfig = (options?: {
  onlyConsumption?: boolean;
}): AvailableCommoditiesShape | AvailableConsumptionCommoditiesShape => {
  const { onlyConsumption } = options ?? {};
  return useSelector(onlyConsumption ? selectAvailableConsumptionCommodities : selectAvailableCommodities);
};

export type UseCanShape = (action: ResourceAction, resource: Resource | Subject | ForcedSubject<Subject>) => boolean;

export const useCan = (): UseCanShape => {
  const ability = useAbility(AbilityContext);

  return useCallback(
    (action, resource) => {
      switch (resource) {
        case Resource.Invoices:
          return ability.can(action, 'invoiceRecords');
        case Resource.Readings:
          return (
            ability.can(action, 'manualRecords') ||
            ability.can(action, 'sensorConsumptionRecords') ||
            ability.can(action, 'sensorValueRecords')
          );
        default:
          return ability.can(action, resource);
      }
    },
    [ability],
  );
};

export type UseHasPermission = (permission: DeprecatedPermission) => boolean;

/**
 * Checks, if active user has permission
 * @returns - function, that takes permission as argument and returns boolean
 * @deprecated Use `useCan` instead.
 */
export const useHasPermission = (): UseHasPermission => {
  const user = useActiveUser();
  const can = useCan();

  return useCallback(
    permission => {
      if (!user) {
        console.error(`User not available`);
        return false;
      }

      if (user.userType === UserRole.Admin || user.userType === UserRole.Magic) return true;

      const [resource = '', action = ''] = permission.split('.');

      if (!(isResource(resource) && isAction(action))) {
        console.error(`Invalid permission: ${permission}`);
        return false;
      }

      return can(action, resource);
    },
    [user, can],
  );
};

/**
 * Checks, if active user has any (||) of the permissions in permissionGroups
 * uses "some" for OR logic
 * @returns - function, that takes permissionGroups as argument and returns boolean
 */
export const useHasAnyPermissionGroups = (): ((
  permissionGroups: readonly (readonly DeprecatedPermission[])[],
) => boolean) => {
  const checkIfHasPermission = useHasPermission();

  return useCallback(
    permissionGroups => permissionGroups.some(permissionGroup => permissionGroup.every(checkIfHasPermission)),
    [checkIfHasPermission],
  );
};
